import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import { usePortalCategories } from '../../hooks/usePortalCategories';
import { usePortalProfile } from '../../hooks/usePortalProfile';
import { usePortalNavigation } from '../../hooks/usePortalNavigation';

/**
 * Container component that manages portal logic and provides render props
 * This component separates business logic from presentation
 */
const PortalContainer = ({ children, render }) => {
  const [redirectBack, setRedirectBack] = useState(false);

  // Use the custom hooks for separated concerns
  const {
    categories,
    activeCategoryId,
    loading: categoriesLoading,
    toggleActiveCategory,
    selectCategoryByIndex,
  } = usePortalCategories();

  const {
    profile,
    showError,
    loading: profileLoading,
    languageId,
  } = usePortalProfile(activeCategoryId);

  // Handle category selection
  const handleCategorySelect = (index, category) => {
    toggleActiveCategory(category.id);
  };

  // Handle back navigation
  const handleBackPress = () => {
    setRedirectBack(true);
  };

  const { focusIndex, navigateToIndex, manuallySetFocus } = usePortalNavigation(
    categories,
    handleCategorySelect,
    handleBackPress
  );

  // Handle manual item clicks (from UI interactions)
  const handleItemClick = (item) => {
    const index = categories.findIndex((category) => category.id === item.id);
    if (index !== -1) {
      manuallySetFocus(index);
      handleCategorySelect(index, categories[index]);
    }
  };

  // Redirect if back was pressed
  if (redirectBack) {
    return <Navigate to={Route.route.LANDING} />;
  }

  // Prepare data to pass to render function
  const portalData = {
    // Categories data
    categories,
    activeCategoryId,
    categoriesLoading,

    // Profile data
    profile,
    showError,
    profileLoading,
    languageId,

    // Navigation state
    focusIndex,
    
    // Combined loading state
    isLoading: categoriesLoading || profileLoading,
    
    // Actions
    onItemClick: handleItemClick,
    navigateToIndex,
    manuallySetFocus,
  };

  // Support both render prop and children patterns
  if (render && typeof render === 'function') {
    return render(portalData);
  }

  if (children && typeof children === 'function') {
    return children(portalData);
  }

  // Default fallback - should not be reached with proper usage
  return children || null;
};

export default PortalContainer;
